import { ContentfulConfigs } from '@vfmfe/foundations';

/**
 * Denormalizes Contentful REST response to match the expected THeader structure (similar to GraphQL)
 */
export function denormalize(data: any) {
    if (!data || !data.items) return [];

    const entryMap = new Map();
    const assetMap = new Map();

    if (data.includes) {
        data.includes.Entry?.forEach((e: any) => entryMap.set(e.sys.id, e));
        data.includes.Asset?.forEach((a: any) => assetMap.set(a.sys.id, a));
    }
    data.items.forEach((e: any) => entryMap.set(e.sys.id, e));

    function resolve(obj: any, depth = 0): any {
        if (depth > 10 || !obj || typeof obj !== 'object') return obj;

        if (obj.sys?.type === 'Link') {
            if (obj.sys.linkType === 'Entry') {
                const entry = entryMap.get(obj.sys.id);
                if (entry) return resolve(entry, depth + 1);
            }
            if (obj.sys.linkType === 'Asset') {
                const asset = assetMap.get(obj.sys.id);
                if (asset) return resolve(asset, depth + 1);
            }
            return obj;
        }

        if (obj.sys?.type === 'Asset') {
            return {
                url: obj.fields?.file?.url ? `https:${obj.fields.file.url}` : undefined,
                description: obj.fields?.description,
                title: obj.fields?.title
            };
        }

        if (obj.fields) {
            const fields = { ...obj.fields };
            for (const [key, value] of Object.entries(fields)) {
                if (Array.isArray(value)) {
                    const resolvedItems = value.map(item => resolve(item, depth + 1));
                    // GraphQL compatibility: add Collection suffix with items array
                    fields[`${key}Collection`] = { items: resolvedItems };
                    fields[key] = resolvedItems;
                } else {
                    fields[key] = resolve(value, depth + 1);
                }
            }
            return fields;
        }

        return obj;
    }

    return data.items.map((item: any) => resolve(item));
}

/**
 * Generic Contentful REST requester
 */
export async function cf<T>(
    params: Record<string, any>,
    config?: ContentfulConfigs
): Promise<T> {
    const { spaceID, environment, accessToken } = config?.contentful ?? {};
    const { preview, ...restParams } = params;

    const host = preview ? 'preview.contentful.com' : 'cdn.contentful.com';
    const url = new URL(`https://${host}/spaces/${spaceID}/environments/${environment}/entries`);

    url.searchParams.append('access_token', accessToken || '');

    Object.entries(restParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            url.searchParams.append(key, String(value));
        }
    });

    const res = await fetch(url.toString());
    if (!res.ok) {
        const text = await res.text();
        throw new Error(`Contentful error: ${res.status} ${text}`);
    }
    const data = await res.json();
    const items = denormalize(data);

    return items as unknown as T;
}
